import React from 'react';

const DescriptionReviewToggle = ({
  showDescription,
  setShowDescription,
  productData,
  reviews,
  totalReviews,
  averageRating,
}) => {
  const getReviewText = (count) => (count === 1 ? 'Review (1)' : `Reviews (${count})`);

  const formatDate = (isoDateString) => {
    const date = new Date(isoDateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderDescription = () => {
    if (!productData.description) {
      return (
        <p className="text-[#4c0f2e] text-sm font-poppins opacity-70">No description available.</p>
      );
    }
    return (
      <div
        className="text-[#2d161e] text-sm font-poppins leading-relaxed"
        dangerouslySetInnerHTML={{ __html: productData.description }}
      />
    );
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const halfStars = rating % 1 >= 0.5 ? 1 : 0;
    const emptyStars = 5 - fullStars - halfStars;

    return (
      <div className="flex text-[#b09287]">
        {Array.from({ length: fullStars }).map((_, i) => (
          <svg key={`full-${i}`} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        {halfStars === 1 && (
          <div className="relative">
            <svg className="w-4 h-4 text-[#e0c3b5]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <svg
              className="absolute top-0 left-0 w-2 h-4 text-[#b09287] overflow-hidden"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        )}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <svg
            key={`empty-${i}`}
            className="w-4 h-4 text-[#e0c3b5]"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  const renderReviews = () => {
    if (reviews.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-[#4c0f2e] text-sm font-poppins mb-4 opacity-70">No reviews yet.</p>
          <button className="px-6 py-3 text-[#4c0f2e] text-sm rounded-lg font-poppins border-2 border-[#b09287] bg-[#ebe2db] hover:bg-[#e0c3b5] transition-all duration-200 shadow-sm">
            Be the first to review
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6 max-h-80 overflow-y-auto pr-2">
        {reviews.map((review) => (
          <div
            key={review._id}
            className="border-b border-[#e0c3b5] pb-5 last:border-none hover:bg-[#ebe2db] rounded-lg p-4 transition-all duration-200"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#e0c3b5] flex items-center justify-center text-[#4c0f2e] text-sm font-poppins font-semibold shadow-sm">
                {(review.userId?.name || review.guestName || 'A').charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-[#4c0f2e] text-sm font-poppins font-semibold">
                  {review.userId?.name || review.guestName || 'Anonymous'}
                </p>
                <p className="text-xs text-[#b09287] font-poppins opacity-80">
                  {formatDate(review.date)}
                </p>
              </div>
            </div>
            <div className="flex mb-3">{renderStars(review.rating)}</div>
            <p className="text-[#2d161e] text-sm leading-relaxed font-poppins">{review.comment}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mt-10  rounded-xl overflow-hidden border border-[#2a081a] shadow-lg">
      <div className="flex border-b-2 border-[#2a081a] ">
        <button
          onClick={() => setShowDescription(true)}
          className={`flex-1 px-6 py-4 font-forum text-sm font-semibold transition-all duration-200 ${
            showDescription
              ? 'bg-[#4c0f2e] text-[#ebe2db] shadow-inner'
              : 'text-[#4c0f2e] hover:bg-[#e0c3b5]'
          }`}
        >
          Description
        </button>
        <button
          onClick={() => setShowDescription(false)}
          className={`flex-1 px-6 py-4 font-forum text-sm font-semibold transition-all duration-200 ${
            !showDescription
              ? 'bg-[#4c0f2e] text-[#ebe2db] shadow-inner'
              : 'text-[#4c0f2e] hover:bg-[#e0c3b5]'
          }`}
        >
          {getReviewText(totalReviews)}
        </button>
      </div>

      <div className="px-6 py-6  min-h-[200px]">
        {showDescription ? renderDescription() : renderReviews()}
      </div>
    </div>
  );
};

export default DescriptionReviewToggle;
